'use client';

import { c as _c } from "react/compiler-runtime";
import { Item, ItemActions, ItemContent, ItemDescription, ItemMedia, ItemTitle } from '@repo/ui/components';
import * as React from 'react';
export type ApiErrorEntry = React.ReactNode | {
  action?: React.ReactNode;
  id?: string;
  message: React.ReactNode;
  title?: React.ReactNode;
};
export type ApiErrorsProps = Omit<React.ComponentProps<typeof Item>, 'children' | 'variant'> & {
  action?: React.ReactNode;
  errors?: ApiErrorEntry | ApiErrorEntry[] | null;
  icon?: React.ReactNode;
  title?: React.ReactNode;
};
export function ApiErrors(t0) {
  const $ = _c(24);
  let action;
  let errors;
  let icon;
  let itemProps;
  let title;
  if ($[0] !== t0) {
    ({
      action,
      errors,
      icon,
      title,
      ...itemProps
    } = t0);
    $[0] = t0;
    $[1] = action;
    $[2] = errors;
    $[3] = icon;
    $[4] = itemProps;
    $[5] = title;
  } else {
    action = $[1];
    errors = $[2];
    icon = $[3];
    itemProps = $[4];
    title = $[5];
  }
  let t1;
  if ($[6] !== errors) {
    t1 = normalizeErrors(errors);
    $[6] = errors;
    $[7] = t1;
  } else {
    t1 = $[7];
  }
  const normalized = t1;
  if (normalized.length === 0) {
    return null;
  }
  let t2;
  if ($[8] !== icon) {
    t2 = icon ? <ItemMedia>{icon}</ItemMedia> : null;
    $[8] = icon;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  let t3;
  if ($[10] !== title) {
    t3 = title ? <ItemTitle>{title}</ItemTitle> : null;
    $[10] = title;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  let t4;
  if ($[12] !== normalized) {
    t4 = normalized.length === 1 ? <SingleErrorContent entry={normalized[0]} /> : <ErrorList entries={normalized} />;
    $[12] = normalized;
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  let t5;
  if ($[14] !== action) {
    t5 = action ? <ItemActions>{action}</ItemActions> : null;
    $[14] = action;
    $[15] = t5;
  } else {
    t5 = $[15];
  }
  let t6;
  if ($[16] !== t3 || $[17] !== t4 || $[18] !== t5) {
    t6 = <ItemContent data-sentry-element="ItemContent" data-sentry-source-file="api-errors.tsx">{t3}{t4}{t5}</ItemContent>;
    $[16] = t3;
    $[17] = t4;
    $[18] = t5;
    $[19] = t6;
  } else {
    t6 = $[19];
  }
  let t7;
  if ($[20] !== itemProps || $[21] !== t2 || $[22] !== t6) {
    t7 = <Item role="alert" variant="danger" {...itemProps} data-sentry-element="Item" data-sentry-component="ApiErrors" data-sentry-source-file="api-errors.tsx">{t2}{t6}</Item>;
    $[20] = itemProps;
    $[21] = t2;
    $[22] = t6;
    $[23] = t7;
  } else {
    t7 = $[23];
  }
  return t7;
}
function ErrorList(t0) {
  const $ = _c(4);
  const {
    entries
  } = t0;
  let t1;
  if ($[0] !== entries) {
    t1 = entries.map(_temp);
    $[0] = entries;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== t1) {
    t2 = <ul className="flex flex-col gap-1" data-sentry-component="ErrorList" data-sentry-source-file="api-errors.tsx">{t1}</ul>;
    $[2] = t1;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  return t2;
}
function _temp(entry, index) {
  const key = isErrorEntryObject(entry) ? entry.id ?? index : index;
  return <li key={key}><SingleErrorContent entry={entry} /></li>;
}
function isErrorEntryObject(entry: ApiErrorEntry): entry is {
  action?: React.ReactNode;
  id?: string;
  message: React.ReactNode;
  title?: React.ReactNode;
} {
  return entry !== null && typeof entry === 'object' && !React.isValidElement(entry) && 'message' in entry;
}
function normalizeErrors(errors: ApiErrorsProps['errors']): ApiErrorEntry[] {
  if (errors == null || errors === false || errors === '') {
    return [];
  }
  if (Array.isArray(errors)) {
    return errors;
  }
  if (typeof errors === 'string') {
    const parts = errors.split('\n').filter(s => s.trim() !== '');
    if (parts.length === 0) {
      return [];
    }
    if (parts.length === 1) {
      return [parts[0]];
    }
    return parts;
  }
  return [errors];
}
function SingleErrorContent(t0) {
  const $ = _c(12);
  const {
    entry
  } = t0;
  if (!isErrorEntryObject(entry)) {
    let t1;
    if ($[0] !== entry) {
      t1 = <ItemDescription className="text-destructive">{entry}</ItemDescription>;
      $[0] = entry;
      $[1] = t1;
    } else {
      t1 = $[1];
    }
    return t1;
  }
  let t1;
  if ($[2] !== entry.title) {
    t1 = entry.title ? <ItemTitle>{entry.title}</ItemTitle> : null;
    $[2] = entry.title;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[4] !== entry.message) {
    t2 = <ItemDescription data-sentry-element="ItemDescription" data-sentry-source-file="api-errors.tsx">{entry.message}</ItemDescription>;
    $[4] = entry.message;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== entry.action) {
    t3 = entry.action ? <ItemActions>{entry.action}</ItemActions> : null;
    $[6] = entry.action;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] !== t1 || $[9] !== t2 || $[10] !== t3) {
    t4 = <>{t1}{t2}{t3}</>;
    $[8] = t1;
    $[9] = t2;
    $[10] = t3;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  return t4;
}